import { useEffect, useRef, useCallback, useState } from "react";
import { useUserSettings } from "@sukha/query";
import { getAssistantAudio } from "@/hooks/assistant/assistantApi";
import { assistantAudioPlayer } from "@/lib/clientAudio/assistantAudioPlayer";
import { useSettingsStore } from "@/stores/general";
import { isNotAllowedError } from "@/lib/utils/audioErrors";
import { isUiLocale, type MemberUiLocale } from "@/i18n/locale";
import Logger from "@/lib/utils/logger";
type AudioMessageType = "phoneNudge" | "phoneNudge:distracted" | "phoneNudge:focused" | "websiteNudge" | "encouragement" | "encouragement:break" | "encouragement:focus" | "encouragement:session_start" | "encouragement:session_end" | "taskHelp" | "meetingReminder" | "general";
interface QueuedAudio {
  text: string;
  onEnded?: () => void;
  type: AudioMessageType;
  id: string;
  contentLocale?: MemberUiLocale;
}

// Global audio URL for singleton pattern
let globalAudioUrl: string | null = null;

// Audio cache for preloaded audio elements (LRU-capped)
const audioCache = new Map<string, HTMLAudioElement>();
const MAX_CACHE_SIZE = 20;

// Move key to most-recent position
function touchCacheKey(key: string) {
  const existing = audioCache.get(key);
  if (!existing) return;
  audioCache.delete(key);
  audioCache.set(key, existing);
}

// Evict least-recently-used items when over capacity
function evictIfNeeded() {
  while (audioCache.size > MAX_CACHE_SIZE) {
    const oldestKey = audioCache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    const audio = audioCache.get(oldestKey);
    if (audio) {
      if (audio.src && audio.src.startsWith("blob:")) {
        URL.revokeObjectURL(audio.src);
      }
      audio.src = "";
    }
    audioCache.delete(oldestKey);
    if (process.env.NODE_ENV !== "production") {
      try {
        // Avoid logging entire text
        Logger.debug("[useAssistantAudio] LRU: evicted cached audio for:", oldestKey.substring(0, 50) + "...");
      } catch {}
    }
  }
}

/** Clears preloaded assistant audio after locale or voice changes. */
export function clearAssistantAudioCache(): void {
  for (const audio of audioCache.values()) {
    if (audio.src && audio.src.startsWith("blob:")) {
      URL.revokeObjectURL(audio.src);
    }
    audio.src = "";
  }
  audioCache.clear();
}

/**
 * Custom hook for managing assistant audio playback
 * @returns Object containing audio controls and state
 */
export function useAssistantAudio() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const {
    data: userSettings,
    isLoading: userSettingsLoading
  } = useUserSettings();
  const meetingAudioSilence = useSettingsStore(state => state.meetingAudioSilence);
  const speakUiLocale = userSettings?.locale && isUiLocale(userSettings.locale) ? userSettings.locale : "en";
  const speakUiLocaleRef = useRef(speakUiLocale);
  speakUiLocaleRef.current = speakUiLocale;

  // Queue state
  const queueRef = useRef<QueuedAudio[]>([]);
  const isProcessingQueue = useRef(false);
  const nextId = useRef(0);
  // Pending guard: prevent near-simultaneous enqueue for same type
  const pendingTypesRef = useRef<Set<AudioMessageType>>(new Set());

  // Preloaded audio state
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadError, setPreloadError] = useState<Error | null>(null);
  const preloadedAudioRef = useRef<HTMLAudioElement | null>(null);

  // Session history for de-duplication and cooldowns
  // Track last play timestamps by message type
  const lastPlayedAtRef = useRef<Record<AudioMessageType, number>>({
    phoneNudge: 0,
    "phoneNudge:distracted": 0,
    "phoneNudge:focused": 0,
    websiteNudge: 0,
    encouragement: 0,
    "encouragement:break": 0,
    "encouragement:focus": 0,
    "encouragement:session_start": 0,
    "encouragement:session_end": 0,
    taskHelp: 0,
    meetingReminder: 0,
    general: 0
  });
  // Track recent texts (basic hashing) to avoid repeating same text in a short window
  const recentTextsRef = useRef<Array<{
    hash: string;
    type: AudioMessageType;
    ts: number;
  }>>([]);
  // Cooldown configuration per type (milliseconds)
  const TYPE_COOLDOWNS: Partial<Record<AudioMessageType, number>> = {
    "encouragement:session_start": 8000,
    "encouragement:session_end": 6000,
    "encouragement:break": 5000,
    "encouragement:focus": 5000,
    encouragement: 4000,
    meetingReminder: 5000,
    phoneNudge: 4000,
    "phoneNudge:distracted": 4000,
    "phoneNudge:focused": 4000,
    websiteNudge: 4000,
    general: 2500
  };
  const typeCooldownsRef = useRef(TYPE_COOLDOWNS);
  const RECENT_TEXT_WINDOW_MS = 10000; // de-dup identical text within 10s
  const maxRecentTexts = 20;
  const normalizeAndHash = useCallback((text: string) => {
    const norm = text.trim().toLowerCase().replace(/\s+/g, " ");
    // Simple hash
    let h = 0;
    for (let i = 0; i < norm.length; i++) {
      h = h * 31 + norm.charCodeAt(i) | 0;
    }
    return h.toString();
  }, []);
  const isWithinCooldown = useCallback((type: AudioMessageType, now: number) => {
    const last = lastPlayedAtRef.current[type] || 0;
    const cooldown = typeCooldownsRef.current[type] ?? 2500;
    return now - last < cooldown;
  }, []);
  const markPlayed = useCallback((type_0: AudioMessageType, text_0: string, now_0: number) => {
    lastPlayedAtRef.current[type_0] = now_0;
    const hash = normalizeAndHash(text_0);
    // prune old entries
    const cutoff = now_0 - RECENT_TEXT_WINDOW_MS;
    recentTextsRef.current = recentTextsRef.current.filter(e => e.ts >= cutoff).slice(-maxRecentTexts);
    recentTextsRef.current.push({
      hash,
      type: type_0,
      ts: now_0
    });
  }, [normalizeAndHash]);
  const isDuplicateTextRecently = useCallback((type_1: AudioMessageType, text_1: string, now_1: number) => {
    const hash_0 = normalizeAndHash(text_1);
    const cutoff_0 = now_1 - RECENT_TEXT_WINDOW_MS;
    return recentTextsRef.current.some(e_0 => e_0.type === type_1 && e_0.hash === hash_0 && e_0.ts >= cutoff_0);
  }, [normalizeAndHash]);

  // Use user settings if available, otherwise fall back to store values
  const isAudioEnabled = userSettings?.assistantAudioEnabled;
  const volume = userSettings?.smartAssistantAudioVolume;

  // Memoize stopAudio to prevent unnecessary effect re-runs
  const stopAudio = useCallback((clearQueue = false) => {
    const audio = assistantAudioPlayer.getInstance();
    // Stop and reset the singleton audio player
    audio.pause();
    audio.currentTime = 0;

    // Clean up any existing blob URLs
    if (audio.src.startsWith("blob:")) {
      URL.revokeObjectURL(audio.src);
    }

    // Clear the audio source
    audio.src = "";
    audio.removeAttribute("src");

    // Clear event listeners
    audio.onended = null;
    audio.onerror = null;
    audio.onloadstart = null;
    audio.oncanplay = null;

    // Clear the global URL reference
    if (globalAudioUrl) {
      URL.revokeObjectURL(globalAudioUrl);
      globalAudioUrl = null;
    }
    if (clearQueue) {
      // Clear queue and call onEnded for any pending items
      queueRef.current.forEach(item => {
        if (item.onEnded) {
          item.onEnded();
        }
      });
      queueRef.current = [];
      pendingTypesRef.current.clear();
    }
    setIsPlaying(false);
    isProcessingQueue.current = false;
  }, []); // No dependencies needed since getInstance() is called inside

  // Cleanup audio resources when component unmounts
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  // Update volume when it changes
  useEffect(() => {
    // Only run on client side to avoid SSR issues
    if (typeof window === "undefined") return;

    // Use singleton's setVolume method to ensure consistent normalization
    assistantAudioPlayer.setVolume(volume);
  }, [volume, userSettingsLoading]);

  /**
   * Check if a specific message type should be played based on user settings
   */
  const shouldPlayMessageType = useCallback((type_2: AudioMessageType): boolean => {
    if (!userSettings) return true;
    switch (type_2) {
      case "phoneNudge":
      case "phoneNudge:distracted":
      case "phoneNudge:focused":
      case "websiteNudge":
        return userSettings.phoneAndWebSiteNudge !== false;
      case "encouragement":
      case "encouragement:break":
      case "encouragement:focus":
      case "encouragement:session_start":
      case "encouragement:session_end":
        return userSettings.wordsOfEncouragement !== false;
      case "taskHelp":
        return userSettings.generalTaskHelp !== false;
      case "meetingReminder":
        return userSettings.meetingRemindersEnabled !== false;
      case "general":
      default:
        return true;
    }
  }, [userSettings]);

  /**
   * Fetches audio data for the given text and plays it
   * @param text The text to convert to speech
   * @param onEnded Optional callback when audio finishes playing
   * @param type Optional type of the message for settings-based filtering
   */
  const processQueue = useCallback(async () => {
    // If already processing or queue is empty, do nothing
    if (isProcessingQueue.current || queueRef.current.length === 0) {
      return;
    }
    if (useSettingsStore.getState().meetingAudioSilence) {
      const cleared = queueRef.current.splice(0);
      cleared.forEach(item_0 => {
        pendingTypesRef.current.delete(item_0.type);
        item_0.onEnded?.();
      });
      isProcessingQueue.current = false;
      setIsLoading(false);
      return;
    }
    isProcessingQueue.current = true;
    const currentItem = queueRef.current[0];
    try {
      setIsLoading(true);
      setError(null);

      // Fetch audio data
      const audioData = await getAssistantAudio(currentItem.text, undefined, currentItem.contentLocale ?? speakUiLocaleRef.current);
      if (!audioData) {
        throw new Error("Failed to get audio data");
      }

      // Create a Blob from the ArrayBuffer and set it as the source
      const audioBlob = new Blob([audioData.buffer], {
        type: audioData.contentType
      });
      const audioUrl = URL.createObjectURL(audioBlob);
      globalAudioUrl = audioUrl; // Store for cleanup

      // Use the singleton audio player
      const audio_0 = assistantAudioPlayer.getInstance();
      audio_0.src = audioUrl;
      // Volume is maintained by the singleton, no need to set it here

      // Set up event handlers for the singleton player
      const handleEnded = () => {
        // Clean up object URL
        if (globalAudioUrl) {
          URL.revokeObjectURL(globalAudioUrl);
          globalAudioUrl = null;
        }

        // Remove the current item from queue and call its callback
        const playedItem = queueRef.current.shift();
        if (playedItem) {
          // Clear pending type guard for this item
          pendingTypesRef.current.delete(playedItem.type);
        }
        if (playedItem?.onEnded) {
          playedItem.onEnded();
        }
        setIsPlaying(false);

        // Process next item in queue
        isProcessingQueue.current = false;
        processQueue();
      };
      const handleError = (event: Event | string) => {
        // Check for NotAllowedError from event.target.error or ErrorEvent (expected when browser blocks autoplay)
        const target = typeof event === "object" && event !== null && "target" in event ? (event as {
          target?: unknown;
        }).target : null;
        const mediaError = target instanceof HTMLMediaElement ? target.error : null;
        const eventError = event instanceof ErrorEvent ? event.error : null;
        const isBlockedByAutoplay = isNotAllowedError(mediaError) || isNotAllowedError(eventError);
        if (!isBlockedByAutoplay) {
          Logger.error("[useAssistantAudio] Audio playback error:", event);
          setError(new Error("Error playing audio"));
        } else {
          Logger.debug("[useAssistantAudio] Audio blocked by browser autoplay policy (expected in background tab or second browser)");
        }

        // Clean up the audio URL
        if (globalAudioUrl) {
          URL.revokeObjectURL(globalAudioUrl);
          globalAudioUrl = null;
        }

        // Remove failed item from queue and continue
        const failedItem = queueRef.current.shift();
        if (failedItem) {
          pendingTypesRef.current.delete(failedItem.type);
        }
        isProcessingQueue.current = false;
        processQueue();
      };

      // Set up event handlers for the singleton player
      audio_0.onended = handleEnded;
      audio_0.onerror = handleError;

      // Play the audio
      await audio_0.play();
      globalAudioUrl = audioUrl;
      setIsPlaying(true);
    } catch (error_0) {
      // NotAllowedError is expected when browser blocks autoplay (e.g. second tab/browser without user interaction)
      if (!isNotAllowedError(error_0)) {
        Logger.error("[useAssistantAudio] Error processing audio queue:", error_0);
        setError(error_0 instanceof Error ? error_0 : new Error("Unknown error"));
      } else {
        Logger.debug("[useAssistantAudio] Audio blocked by browser autoplay policy (expected in background tab or second browser)");
      }

      // Clean up the audio URL
      if (globalAudioUrl) {
        URL.revokeObjectURL(globalAudioUrl);
        globalAudioUrl = null;
      }

      // Remove failed item from queue
      queueRef.current.shift();
      isProcessingQueue.current = false;

      // Try to process next item even if current one failed
      processQueue();
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies needed - uses refs and singleton which are stable

  const fetchAndPlayAudio = useCallback(async (text_2: string, type_3: AudioMessageType = "general", onEnded?: () => void, contentLocale?: MemberUiLocale) => {
    if (!text_2.trim()) {
      Logger.warn("[useAssistantAudio] Skipping empty spoken line");
      if (onEnded) onEnded();
      return;
    }

    // Note: Mobile restriction removed - each device handles its own audio
    // Allow audio to play in background for assistant messages during sessions
    // The activeTab check was preventing background audio playback
    // Skip if audio is disabled in user settings or store
    if (!isAudioEnabled) {
      Logger.debug("[useAssistantAudio] Audio is disabled in user settings, skipping playback");
      if (onEnded) onEnded();
      return;
    }

    // Skip if still loading user settings
    if (userSettingsLoading) {
      Logger.debug("[useAssistantAudio] Waiting for user settings to load...");
      return;
    }
    if (useSettingsStore.getState().meetingAudioSilence) {
      Logger.debug("[useAssistantAudio] Meeting audio silence on, skipping playback");
      if (onEnded) onEnded();
      return;
    }

    // Check if this message type should be played based on user settings
    if (!shouldPlayMessageType(type_3)) {
      Logger.debug(`[useAssistantAudio] Skipping ${type_3} message as it's disabled in user settings`);
      if (onEnded) onEnded();
      return;
    }
    const now_2 = Date.now();
    // Enforce per-type cooldown regardless of queue state
    if (isWithinCooldown(type_3, now_2)) {
      Logger.debug(`[useAssistantAudio] Skipping ${type_3} due to cooldown`);
      if (onEnded) onEnded();
      return;
    }

    // Avoid enqueueing identical text of same type within recent window
    if (isDuplicateTextRecently(type_3, text_2, now_2)) {
      Logger.debug(`[useAssistantAudio] Skipping ${type_3} due to recent identical text`);
      if (onEnded) onEnded();
      return;
    }

    // Check for duplicate message of the same type already in the queue
    const hasDuplicateInQueue = queueRef.current.some(item_1 => item_1.type === type_3);
    if (hasDuplicateInQueue) {
      Logger.debug(`[useAssistantAudio] Skipping duplicate queued message of type: ${type_3}`);
      if (onEnded) onEnded();
      return;
    }

    // Prevent near-simultaneous enqueue for the same type
    if (pendingTypesRef.current.has(type_3)) {
      Logger.debug(`[useAssistantAudio] Skipping ${type_3} because a pending request exists`);
      if (onEnded) onEnded();
      return;
    }

    // Create a new queue item
    const newItem: QueuedAudio = {
      id: `audio-${nextId.current++}`,
      text: text_2,
      onEnded,
      type: type_3,
      contentLocale
    };

    // Add to queue (limit to 5 items, remove oldest if needed)
    if (queueRef.current.length >= 5) {
      Logger.debug(`[useAssistantAudio] Queue full, removing oldest item`);
      // Remove the first item (oldest) if queue is full
      const removed = queueRef.current.shift();
      // Call onEnded for the removed item if it exists
      if (removed?.onEnded) {
        removed.onEnded();
      }
    }

    // Record as played to start cooldown immediately on enqueue
    markPlayed(type_3, text_2, now_2);
    // Mark as pending to block concurrent enqueues of same type
    pendingTypesRef.current.add(type_3);
    queueRef.current.push(newItem);
    Logger.debug(`[useAssistantAudio] Added to queue (${queueRef.current.length} items)`);

    // Start processing if not already doing so
    if (!isProcessingQueue.current) {
      processQueue();
    }
  }, [isAudioEnabled, userSettingsLoading, shouldPlayMessageType, processQueue, isWithinCooldown, isDuplicateTextRecently, markPlayed]);

  // Preload audio without playing
  const preloadAudio = useCallback(async (text_3: string, _type: AudioMessageType = "general", contentLocale_0?: MemberUiLocale) => {
    if (!text_3.trim()) {
      return;
    }
    // Note: Mobile restriction removed - each device handles its own audio
    // Allow preloading in background for assistant messages during sessions
    if (!isAudioEnabled) {
      Logger.debug(`[useAssistantAudio] Audio is disabled, skipping preload`);
      return;
    }

    // Check if already cached and touch to mark recent
    if (audioCache.has(text_3)) {
      touchCacheKey(text_3);
      Logger.debug(`[useAssistantAudio] Audio already cached for: ${text_3.substring(0, 50)}`);
      return;
    }
    setIsPreloading(true);
    setPreloadError(null);
    try {
      Logger.debug(`[useAssistantAudio] Preloading audio for: ${text_3.substring(0, 50)}`);
      const audioData_0 = await getAssistantAudio(text_3, undefined, contentLocale_0 ?? speakUiLocaleRef.current);

      // Convert ArrayBuffer to blob URL
      const audioBlob_0 = new Blob([audioData_0.buffer], {
        type: audioData_0.contentType
      });
      const audioUrl_0 = URL.createObjectURL(audioBlob_0);

      // Create and configure audio element
      const audio_1 = new Audio();
      audio_1.crossOrigin = "anonymous";
      audio_1.preload = "auto";
      // Set volume using singleton's normalization (for cached audio, we'll set it when playing)
      // Note: This is for preloaded/cached audio, volume will be set when playing via playPreloadedAudio
      audio_1.src = audioUrl_0;

      // Wait for audio to be ready
      await new Promise<void>((resolve, reject) => {
        const handleCanPlay = () => {
          audio_1.removeEventListener("canplaythrough", handleCanPlay);
          audio_1.removeEventListener("error", handleError_0);
          resolve();
        };
        const handleError_0 = (_event: Event) => {
          audio_1.removeEventListener("canplaythrough", handleCanPlay);
          audio_1.removeEventListener("error", handleError_0);
          reject(new Error("Failed to preload audio"));
        };
        audio_1.addEventListener("canplaythrough", handleCanPlay);
        audio_1.addEventListener("error", handleError_0);
      });

      // Cache the ready audio element (refresh position if existed)
      if (audioCache.has(text_3)) {
        audioCache.delete(text_3);
      }
      audioCache.set(text_3, audio_1);
      evictIfNeeded();
      Logger.debug(`[useAssistantAudio] Audio preloaded and cached successfully. Cache size: ${audioCache.size}`);
    } catch (error_1) {
      Logger.error("[useAssistantAudio] Error preloading audio:", error_1);
      setPreloadError(error_1 instanceof Error ? error_1 : new Error("Unknown preload error"));
    } finally {
      setIsPreloading(false);
    }
  }, [isAudioEnabled] // Volume is handled by singleton, no need in dependencies
  );

  // Play preloaded audio instantly
  const playPreloadedAudio = useCallback(async (text_4: string, onEnded_0?: () => void) => {
    // Note: Mobile restriction removed - each device handles its own audio
    if (!isAudioEnabled) {
      Logger.debug("[useAssistantAudio] Audio is disabled, skipping playback");
      if (onEnded_0) onEnded_0();
      return;
    }
    if (useSettingsStore.getState().meetingAudioSilence) {
      Logger.debug("[useAssistantAudio] Meeting audio silence on, skipping preloaded playback");
      if (onEnded_0) onEnded_0();
      return;
    }
    if (process.env.NODE_ENV !== "production") {
      Logger.debug(`[useAssistantAudio] Current audio cache size: ${audioCache.size}`);
      Logger.debug(`[useAssistantAudio] Cache keys: ${Array.from(audioCache.keys()).map(k => k.substring(0, 30) + "...")}`);
    }
    const cachedAudio = audioCache.get(text_4);
    if (!cachedAudio) {
      Logger.warn("❌ Audio not preloaded for:", text_4.substring(0, 50) + "...");
      Logger.warn("🔄 Falling back to regular fetchAndPlayAudio");
      // Fallback to regular playAudio
      return fetchAndPlayAudio(text_4, "general", onEnded_0);
    }

    // Touch to mark as most-recently-used
    touchCacheKey(text_4);
    Logger.debug(`[useAssistantAudio] Found cached audio! Playing instantly...`);

    // Stop any currently playing preloaded audio
    if (preloadedAudioRef.current && !preloadedAudioRef.current.paused) {
      preloadedAudioRef.current.pause();
      preloadedAudioRef.current.currentTime = 0;
    }
    try {
      // Reset audio to beginning
      cachedAudio.currentTime = 0;
      // Set volume using singleton's normalization method
      // Get the current volume from the singleton's instance to ensure consistency
      const singletonAudio = assistantAudioPlayer.getInstance();
      cachedAudio.volume = singletonAudio.volume;

      // Set up event handlers
      const handleEnded_0 = () => {
        cachedAudio.removeEventListener("ended", handleEnded_0);
        cachedAudio.removeEventListener("error", handleError_1);
        setIsPlaying(false);
        preloadedAudioRef.current = null;
        if (onEnded_0) onEnded_0();
      };
      const handleError_1 = (_event_0: Event) => {
        cachedAudio.removeEventListener("ended", handleEnded_0);
        cachedAudio.removeEventListener("error", handleError_1);
        Logger.error("[useAssistantAudio] Error playing preloaded audio:", _event_0);
        setError(new Error("Failed to play preloaded audio"));
        setIsPlaying(false);
        preloadedAudioRef.current = null;
      };
      cachedAudio.addEventListener("ended", handleEnded_0);
      cachedAudio.addEventListener("error", handleError_1);

      // Play the cached audio
      await cachedAudio.play();
      preloadedAudioRef.current = cachedAudio;
      setIsPlaying(true);
      Logger.debug("[useAssistantAudio] Playing preloaded audio instantly");
    } catch (error_2) {
      Logger.error("[useAssistantAudio] Error playing preloaded audio:", error_2);
      setError(error_2 instanceof Error ? error_2 : new Error("Unknown playback error"));
    }
  }, [isAudioEnabled, fetchAndPlayAudio] // Volume is handled by singleton, no need in dependencies
  );

  // Stop preloaded audio
  const stopPreloadedAudio = useCallback(() => {
    if (preloadedAudioRef.current && !preloadedAudioRef.current.paused) {
      preloadedAudioRef.current.pause();
      preloadedAudioRef.current.currentTime = 0;
      setIsPlaying(false);
      preloadedAudioRef.current = null;
      Logger.debug("[useAssistantAudio] Stopped preloaded audio");
    }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const audio_2 = assistantAudioPlayer.getInstance();
    audio_2.muted = meetingAudioSilence;
    if (meetingAudioSilence) {
      stopAudio(true);
      stopPreloadedAudio();
    }
  }, [meetingAudioSilence, stopAudio, stopPreloadedAudio]);

  // Clear audio cache
  const clearAudioCache = useCallback((text_5?: string) => {
    // Add debug trace to see what's calling this
    Logger.debug(`[useAssistantAudio] clearAudioCache called: ${text_5 ? `for: ${text_5.substring(0, 50)}...` : "ALL AUDIO"}`);
    if (text_5) {
      // Clear specific cached audio
      const audio_3 = audioCache.get(text_5);
      if (audio_3) {
        if (audio_3.src.startsWith("blob:")) {
          URL.revokeObjectURL(audio_3.src);
        }
        audio_3.src = "";
        audioCache.delete(text_5);
        Logger.debug(`[useAssistantAudio] Cleared cached audio for: ${text_5.substring(0, 50)}`);
      }
    } else {
      // Clear all cached audio
      Logger.debug(`[useAssistantAudio] ⚠️ CLEARING ALL CACHED AUDIO - Cache size before: ${audioCache.size}`);
      audioCache.forEach(audio_4 => {
        if (audio_4.src.startsWith("blob:")) {
          URL.revokeObjectURL(audio_4.src);
        }
        audio_4.src = "";
      });
      audioCache.clear();
      Logger.debug("[useAssistantAudio] ❌ Cleared all cached audio");
    }
  }, []);

  // Check if audio is preloaded
  const isPreloaded = useCallback((text_6: string) => {
    return audioCache.has(text_6);
  }, []);

  // Clean up on unmount (but keep cache for onboarding)
  useEffect(() => {
    return () => {
      stopAudio(true); // Clear queue on unmount
      stopPreloadedAudio(); // Stop any preloaded audio
      // NOTE: Not clearing cache automatically - let components manage cache lifecycle
    };
  }, [stopAudio, stopPreloadedAudio]);
  return {
    // State
    isPlaying,
    isLoading,
    error,
    // Methods
    playAudio: fetchAndPlayAudio,
    stopAudio,
    // NEW: Preloading methods
    preloadAudio,
    playPreloadedAudio,
    stopPreloadedAudio,
    clearAudioCache,
    isPreloaded,
    // Preloading state
    isPreloading,
    preloadError

    // Current audio element (use with caution)
  };
}
export default useAssistantAudio;